import { Controller } from "@hotwired/stimulus"

// Dernière recherche sauvegardée (critères du formulaire, sans la date :
// la suggestion de reprise se fait toujours avec la date du jour).
const LAST_SEARCH_KEY = "lastSearch:v1"

export default class extends Controller {
  static targets = ["map", "select", "commune", "suggestions", "radius", "radiusChip", "forestsToggle", "progressContainer", "progressBar", "progressLabel", "results", "threshold", "forecastResults", "date", "obsToggle", "extToggle", "seasonList", "seasonDate", "stopButton", "hero", "heroImage", "heroName", "heroSci", "heroAttr", "lastSearch", "lastSearchLabel", "generateButton", "liveBadge", "liveBadgeText"]
  static values = { mushroomId: Number, mushrooms: Array }

  connect() {
    this.initMap()
    this.center = null
    this.radiusKm = null
    this.cancelGeneration = false
    this._fetchControllers = []
    this.selectTarget.addEventListener("change", () => { this.reloadData(); this.updateSeasonList(); this.updateHero() })
    // Autocomplétion de commune (API Découpage administratif) : suggestions
    // pendant la frappe ; sélectionner centre la carte et précharge les bois.
    this.communeTarget.addEventListener("input", () => this.scheduleSuggest())
    this.communeTarget.addEventListener("change", () => this.autoCenter())
    this.communeTarget.addEventListener("blur", () => setTimeout(() => this.hideSuggestions(), 150))
    this.communeTarget.addEventListener("keydown", (e) => this.suggestionsKeydown(e))
    // Le rayon se choisit par pastilles (setRadius) : état visuel initial.
    this.syncRadiusChips()
    this.map.on("moveend", () => { this.reloadData(); this.scheduleSeasonRefresh() })
    if (this.hasObsToggleTarget) this.obsToggleTarget.addEventListener('change', () => this.reloadData())
    if (this.hasExtToggleTarget) this.extToggleTarget.addEventListener('change', () => this.reloadData())
    if (this.hasDateTarget) this.dateTarget.addEventListener('change', () => this.updateSeasonList())
    this.updateSeasonList()
    this.updateHero()
    this.showLastSearchSuggestion()
    this.reloadData()
  }

  // --- Dernière recherche : suggestion de reprise à la visite suivante ---

  saveLastSearch() {
    try {
      localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify({
        mushroomId: this.selectTarget?.value || null,
        commune: (this.communeTarget?.value || "").trim(),
        radius: this.radiusTarget?.value || null,
        threshold: this.thresholdTarget?.value || null
      }))
    } catch (e) {}
  }

  readLastSearch() {
    try {
      const data = JSON.parse(localStorage.getItem(LAST_SEARCH_KEY))
      return data && (data.mushroomId || data.commune) ? data : null
    } catch (e) { return null }
  }

  showLastSearchSuggestion() {
    if (!this.hasLastSearchTarget) return
    const data = this.readLastSearch()
    if (!data) return
    const m = this.mushroomById(data.mushroomId)
    const parts = []
    if (m) parts.push(m.name)
    if (data.commune) parts.push(data.radius ? `${data.commune} (${data.radius} km)` : data.commune)
    if (!parts.length) return
    this.lastSearchLabelTarget.textContent = parts.join(" · ")
    this.lastSearchTarget.hidden = false
  }

  // Préremplit le formulaire avec la dernière recherche — date du jour, jamais
  // celle d'origine — puis relance directement la génération.
  applyLastSearch() {
    const data = this.readLastSearch()
    if (!data) return
    if (data.mushroomId && this.mushroomById(data.mushroomId)) {
      this.selectTarget.value = String(data.mushroomId)
      this.selectTarget.dispatchEvent(new Event("change"))
    }
    if (this.hasCommuneTarget) this.communeTarget.value = data.commune || ""
    if (data.radius && this.hasRadiusTarget) { this.radiusTarget.value = data.radius; this.syncRadiusChips() }
    if (data.threshold && this.hasThresholdTarget) this.thresholdTarget.value = data.threshold
    if (this.hasDateTarget) {
      const now = new Date()
      this.dateTarget.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
      this.updateSeasonList()
    }
    this.generateForests()
  }

  dismissLastSearch() {
    try { localStorage.removeItem(LAST_SEARCH_KEY) } catch (e) {}
    if (this.hasLastSearchTarget) this.lastSearchTarget.hidden = true
  }

  // Navigation Turbo : la page change mais le JS survit — sans ça, une
  // recherche en cours continuerait à enchaîner les lots serveur pour un
  // DOM détaché que personne ne verra. Même mécanique que le bouton Stopper.
  disconnect() {
    clearTimeout(this._suggestTimer)
    this.stopGeneration()
  }

  // Retrouve un champignon (des données injectées) par son id.
  mushroomById(id) {
    const list = this.hasMushroomsValue ? this.mushroomsValue : []
    return list.find((m) => String(m.id) === String(id)) || null
  }

  // Grande photo du champignon sélectionné, en tête de sidebar.
  updateHero() {
    if (!this.hasHeroTarget) return
    const id = this.selectTarget?.value || this.mushroomIdValue
    const m = this.mushroomById(id)
    if (!m || !m.img) { this.heroTarget.hidden = true; return }
    this.heroTarget.hidden = false
    this.heroImageTarget.src = m.img
    this.heroImageTarget.alt = m.name
    if (this.hasHeroNameTarget) this.heroNameTarget.textContent = m.name
    if (this.hasHeroSciTarget) this.heroSciTarget.textContent = m.sci || ''
    if (this.hasHeroAttrTarget) this.heroAttrTarget.textContent = m.attr || ''
  }

  // Met à jour la liste des champignons en saison pour la date sélectionnée,
  // classée par probabilité de trouvaille au point courant (centre de la zone
  // ou centre de la carte). Rendu en deux temps : liste immédiate (ordre
  // alphabétique), puis tri + badges dès que le serveur a répondu.
  async updateSeasonList() {
    if (!this.hasSeasonListTarget) return
    const list = this.hasMushroomsValue ? this.mushroomsValue : []
    const date = this.selectedDate()
    const month = date.getMonth() + 1 // 1..12
    if (this.hasSeasonDateTarget) this.seasonDateTarget.textContent = this.formatDateFr(date)

    const inSeason = list.filter((m) => this.monthInSeason(month, m.s, m.e))
    this.renderSeasonList(inSeason, null)
    if (inSeason.length === 0) return

    const c = this.center || (this.map && this.map.getCenter())
    if (!c) return
    const token = (this._seasonToken = (this._seasonToken || 0) + 1)
    try {
      const at = this.hasDateTarget && this.dateTarget.value ? `&at=${this.dateTarget.value}` : ''
      const res = await fetch(`/mushrooms/season_rankings?lat=${c.lat}&lng=${c.lng}${at}`, { headers: { Accept: 'application/json' } })
      if (!res.ok) return
      const json = await res.json()
      if (token !== this._seasonToken) return // une requête plus récente est partie
      const scores = new Map((json.rankings || []).map((r) => [r.id, r.probability || 0]))
      const sorted = [...inSeason].sort((a, b) => (scores.get(b.id) || 0) - (scores.get(a.id) || 0))
      this.renderSeasonList(sorted, scores)
    } catch (e) { /* la liste alphabétique reste affichée */ }
  }

  renderSeasonList(items, scores, opts = {}) {
    const ul = this.seasonListTarget
    ul.innerHTML = ""
    if (!items || items.length === 0) {
      const li = document.createElement('li')
      li.className = 'text-forest-400 text-sm italic'
      li.textContent = 'Aucun champignon en saison à cette date'
      ul.appendChild(li)
      return
    }
    const currentId = String(this.selectTarget?.value || this.mushroomIdValue || "")
    items.forEach((m) => {
      const li = document.createElement('li')
      const active = String(m.id) === currentId
      li.className = `list-row ${active ? 'bg-forest-50 ring-1 ring-forest-400' : ''}`
      const score = scores ? scores.get(m.id) : null
      const badge = score != null
        ? `<span class="${this.probBadgeClass(score)}" title="Probabilité ici (météo, saison, sol, altitude)">${Math.round(score * 100)}%</span>`
        : `<span class="prob-badge bg-forest-50 text-forest-300" title="Calcul en cours…">…</span>`
      const thumb = m.img
        ? `<img src="${this.escapeHtml(m.img)}" alt="" loading="lazy" class="size-8 shrink-0 rounded-md object-cover ring-1 ring-forest-100">`
        : `<span class="size-8 shrink-0 rounded-md bg-forest-50 grid place-items-center text-forest-300 text-sm">🍄</span>`
      li.innerHTML = `
        <span class="flex items-center gap-2 min-w-0">${badge}${thumb}
          <span class="truncate"><span class="font-semibold text-forest-900">${this.escapeHtml(m.name)}</span>
            <span class="text-forest-400 italic text-xs">${this.escapeHtml(m.sci)}</span></span></span>
        <span class="text-[11px] text-forest-500 font-medium whitespace-nowrap shrink-0">${this.seasonLabel(m.s, m.e)}</span>`
      li.addEventListener('click', () => this.selectMushroom(m.id))
      ul.appendChild(li)
    })
  }

  // Rafraîchit le classement quand la carte s'arrête de bouger (debounce :
  // un déplacement génère plusieurs moveend rapprochés).
  scheduleSeasonRefresh() {
    clearTimeout(this._seasonTimer)
    this._seasonTimer = setTimeout(() => this.updateSeasonList(), 800)
  }

  selectMushroom(id) {
    if (!this.hasSelectTarget) return
    this.selectTarget.value = String(id)
    this.selectTarget.dispatchEvent(new Event('change')) // déclenche reloadData + updateSeasonList
  }

  selectedDate() {
    const v = this.hasDateTarget ? this.dateTarget.value : null
    if (v) {
      const d = new Date(`${v}T00:00:00`)
      if (!isNaN(d.getTime())) return d
    }
    return new Date()
  }

  // Même logique que Mushroom#in_season? (gère les saisons à cheval sur l'année).
  monthInSeason(month, start, end) {
    if (start == null || end == null) return true
    return start <= end ? (month >= start && month <= end) : (month >= start || month <= end)
  }

  seasonLabel(start, end) {
    const M = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc']
    if (start == null || end == null) return ''
    return `${M[start - 1]}–${M[end - 1]}`
  }

  formatDateFr(d) {
    try { return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) } catch (e) { return '' }
  }

  escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
  }

  initMap() {
    const L = window.L
    this.map = L.map(this.mapTarget).setView([46.7111, 1.7191], 6) // France
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: "© OpenStreetMap"
    }).addTo(this.map)
    this.layer = L.layerGroup().addTo(this.map)
    this.circleLayer = L.layerGroup().addTo(this.map)
    this.forestsLayer = L.layerGroup().addTo(this.map)
    this.highlightLayer = L.layerGroup().addTo(this.map)
    this.externalsLayer = L.layerGroup().addTo(this.map)
  }

  apiUrl() {
    const mushroomId = this.selectTarget.value || this.mushroomIdValue
    const params = new URLSearchParams()
    if (this.center && this.radiusKm) {
      params.set("center", `${this.center.lat},${this.center.lng}`)
      params.set("radius_km", `${this.radiusKm}`)
    } else {
      const b = this.map.getBounds()
      params.set("bounds", `${b.getSouth()},${b.getNorth()},${b.getWest()},${b.getEast()}`)
    }
    params.set("include_prob", "1")
    return `/mushrooms/${mushroomId}/sightings.json?${params.toString()}`
  }

  apiUrlExternal() {
    const mushroomId = this.selectTarget.value || this.mushroomIdValue
    const params = new URLSearchParams()
    if (this.center && this.radiusKm) {
      params.set("center", `${this.center.lat},${this.center.lng}`)
      params.set("radius_km", `${this.radiusKm}`)
    } else {
      const b = this.map.getBounds()
      params.set("bounds", `${b.getSouth()},${b.getNorth()},${b.getWest()},${b.getEast()}`)
    }
    params.set("include_prob", "1")
    return `/mushrooms/${mushroomId}/external_sightings.json?${params.toString()}`
  }

  async reloadData() {
    this.layer.clearLayers()
    this.externalsLayer.clearLayers()
    // Observations locales
    if (this.hasObsToggleTarget && this.obsToggleTarget.checked) {
      const url = this.apiUrl()
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const geojson = await response.json()
      this.renderFeatures(geojson)
    }
    // Observations externes (GBIF)
    if (this.hasExtToggleTarget && this.extToggleTarget.checked) {
      const gj2 = await this.loadExternalGeoJSON()
      this.renderExternalFeatures(gj2)
    }
  }

  async toggleForests(event) {
    const checked = event.target.checked
    if (!checked) {
      this.forestsLayer.clearLayers()
      return
    }
    // Ne rien charger automatiquement : laisser le bouton déclencher la génération
  }

  async loadForests() {
    const L = window.L
    this.forestsLayer.clearLayers()
    this.cancelGeneration = false
    // reset previous controllers
    if (this._overpassAbort) { try { this._overpassAbort.abort() } catch (e) {} }
    this._fetchControllers.forEach((c) => { try { c.abort() } catch (e) {} })
    this._fetchControllers = []
    const t0 = performance.now()
    // Délimitation : si une zone (cercle) est définie, sa bbox englobante,
    // sinon le rectangle visible. Les polygones viennent de la base locale
    // (/forests, cache-aside sur Overpass) : instantané pour une zone connue.
    let south, west, north, east
    if (this.center && this.radiusKm) {
      const dLat = this.radiusKm / 111.0
      const dLng = this.radiusKm / (111.0 * Math.cos(this.center.lat * Math.PI / 180))
      south = this.center.lat - dLat; north = this.center.lat + dLat
      west = this.center.lng - dLng; east = this.center.lng + dLng
    } else {
      const b = this.map.getBounds()
      south = b.getSouth(); west = b.getWest(); north = b.getNorth(); east = b.getEast()
    }
    this.showProgress(0, 1)
    const overAC = new AbortController()
    this._overpassAbort = overAC
    console.time("forests_fetch")
    let data
    try {
      const searchMushroomId = this.selectTarget?.value || this.mushroomIdValue
      const res = await fetch(`/forests?south=${south}&west=${west}&north=${north}&east=${east}${searchMushroomId ? `&mushroom_id=${searchMushroomId}` : ''}`, { signal: overAC.signal })
      if (res.status === 422) {
        console.timeEnd("forests_fetch")
        this.flashStatus('Zone de recherche invalide ou trop large', 2000)
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      data = await res.json()
    } catch (e) {
      console.timeEnd("forests_fetch")
      if (e.name === 'AbortError') return // annulation volontaire (stop/déplacement) : pas d'erreur
      console.warn("forests_failed", e)
      this.flashStatus('Service forêts indisponible, réessayez')
      return
    }
    console.timeEnd("forests_fetch")
    const forests = (data && Array.isArray(data.forests)) ? data.forests : []
    if (forests.length === 0) {
      this.flashStatus(data?.error ? 'Service forêts indisponible, réessayez' : 'Aucune forêt dans cette zone', 1500)
      return
    }

    let processed = 0
    const total = forests.length || 1
    this.setSearchStage(`🔎 Analyse de ${forests.length} bois…`)
    this.showProgress(0, total)

    const mushroomId = this.selectTarget.value || this.mushroomIdValue
    const results = []
    // Progressive list rendering
    if (this.resultsTarget) this.resultsTarget.innerHTML = ""
    // Un POST par lot de 25 bois (au lieu d'une requête par bois) : le
    // serveur calcule en parallèle et partage son cache avec l'ancien
    // endpoint. Lots volontairement petits : chaque requête doit tenir
    // sous les 30 s du routeur Heroku même à froid. Les bois que le
    // serveur n'a pas eu le temps de calculer reviennent `pending` et
    // sont redemandés une fois (caches alors chauds).
    const chunkSize = 25
    const work = []
    for (let i = 0; i < forests.length; i += chunkSize) work.push(forests.slice(i, i + chunkSize))
    const retried = new Set()

    const fetchBatch = async (batch) => {
      const at = this.dateTarget?.value
      const payload = {
        at: at || null,
        threshold: this.getThreshold(),
        forests: batch.map((f) => ({
          id: f.id, lat: f.centroid_lat, lng: f.centroid_lng,
          forest_type: f.forest_type || null, forest_essence: f.essence || null
        }))
      }
      const ac = new AbortController(); this._fetchControllers.push(ac)
      const r = await fetch(`/mushrooms/${mushroomId}/probabilities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify(payload),
        signal: ac.signal
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const json = await r.json()
      return new Map((json.results || []).map((x) => [String(x.id), x]))
    }

    let batchIndex = 0
    while (work.length) {
      const batch = work.shift()
      if (this.cancelGeneration) { this.progressLabelTargets.forEach((el) => { el.textContent = 'Annulé' }); break }
      console.time(`batch_${batchIndex}`)
      let scores = new Map()
      try {
        scores = await fetchBatch(batch)
      } catch (e) {
        console.timeEnd(`batch_${batchIndex}`)
        if (e.name === 'AbortError') break
        console.warn('probabilities_batch_failed', e)
        processed += batch.length
        this.showProgress(processed, total)
        batchIndex += 1
        continue
      }
      console.timeEnd(`batch_${batchIndex}`)
      // Bois non calculés dans le budget de temps serveur : re-tentés une
      // fois en fin de file (les caches seront chauds), puis abandonnés.
      const pending = batch.filter((f) => scores.get(String(f.id))?.pending && !retried.has(f.id))
      if (pending.length) {
        pending.forEach((f) => retried.add(f.id))
        work.push(pending)
      }
      const outputs = []
      for (const f of batch) {
        const latlngs = f.geometry
        if (!latlngs || latlngs.length < 3) continue
        const res = scores.get(String(f.id))
        if (res?.pending) continue
        const prob = res?.probability || 0
        if (res?.below_threshold || prob < this.getThreshold()) continue
        outputs.push({ value: {
          id: f.id, latlngs, cLat: f.centroid_lat, cLng: f.centroid_lng, prob,
          breakdown: res?.breakdown || {}, ft: f.forest_type || null,
          detail: f.forest_type_detail || null, species: f.essence || null
        } })
      }
      processed += batch.length - outputs.length - pending.length
      this.showProgress(processed, total)
      for (const out of outputs) {
        processed += 1
        if (processed % 20 === 0) this.showProgress(processed, total)
        if (!out.value) continue
        const { id, latlngs, cLat, cLng, prob, breakdown, ft, detail, species } = out.value
        const percent = Math.round(prob * 100)
        // Parcelle à 100 % : traitement « trésor » — or, lueur pulsante (CSS
        // .treasure-poly) et étincelle au centroïde.
        const isTreasure = percent >= 100
        const color = isTreasure ? '#d4af37' : this.colorForProbability(prob)
        const poly = L.polygon(latlngs, isTreasure
          ? { color: '#b8860b', fillColor: '#ffd700', fillOpacity: 0.45, weight: 2, className: 'treasure-poly' }
          : { color, fillColor: color, fillOpacity: 0.3, weight: 1 })
        const ftLabel = this.translateForestType(ft)
        const containerId = `obs_${Math.random().toString(36).slice(2)}`
        const heading = this.bearing(this.map.getCenter().lat, this.map.getCenter().lng, cLat, cLng)
        const gmapsNav = `https://www.google.com/maps/dir/?api=1&destination=${cLat},${cLng}&travelmode=driving`
        const gsv = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${cLat},${cLng}&heading=${Math.round(heading)}&pitch=5&fov=80`
        const html = `
          <div style="min-width: 260px">
            <div class="flex items-center justify-between gap-2">
              <div class="font-bold text-forest-900">${isTreasure ? '💰 Coin en or' : '🌲 Bois propice'} <span class="font-normal text-forest-500 capitalize">· ${ftLabel}</span></div>
              <span class="${this.probBadgeClass(prob)}">${percent}%</span>
            </div>
            ${detail ? `<div class="text-xs text-forest-600 mt-0.5">${detail}</div>` : ''}
            <div class="text-xs text-forest-500 mt-0.5">Essence principale : ${species ? species : 'inconnue'}</div>
            ${this.breakdownHtml(breakdown)}
            <div class="mt-2 pt-2 border-t border-forest-100 text-sm flex gap-3">
              <a href="${gmapsNav}" target="_blank">🚗 Itinéraire</a>
              <a href="${gsv}" target="_blank">👁️ Street View</a>
            </div>
            <div class="mt-2 text-sm">
              <div class="font-semibold text-forest-900 mb-1">Observations récentes à proximité</div>
              <div id="${containerId}" class="text-forest-600">Chargement…</div>
            </div>
          </div>`
        poly.bindPopup(html)
        poly.on('popupopen', () => {
          this.loadNearbySightings(containerId, cLat, cLng)
          this.trackForestClick(id, mushroomId, cLat, cLng)
        })
        poly.addTo(this.forestsLayer)
        if (isTreasure) {
          L.marker([cLat, cLng], {
            icon: L.divIcon({ className: 'treasure-marker', html: '<span>✨</span>', iconSize: [24, 24], iconAnchor: [12, 12] }),
            interactive: false
          }).addTo(this.forestsLayer)
        }
        this.flashPolygon(poly)
        const item = { prob, center: [cLat, cLng], ft: ftLabel, latlngs, species }
        results.push(item)
        this.appendResult(item)
        this.bumpLiveCounter(results.length)
      }
      batchIndex += 1
    }

    this.showProgress(total, total)
    if (!this.cancelGeneration) this.showSearchToast(results)
    console.log("generation_total_ms", Math.round(performance.now() - t0))
    // List already filled progressively
  }

  async generateForests() {
    // Mémorise les critères pour la suggestion « reprendre » de la prochaine
    // visite, et replie la suggestion devenue inutile.
    this.saveLastSearch()
    if (this.hasLastSearchTarget) this.lastSearchTarget.hidden = true
    // Si la case n'est pas cochée, on l'active (affichage) puis on génère
    if (!this.forestsToggleTarget.checked) {
      this.forestsToggleTarget.checked = true
    }
    // Si une commune est saisie mais pas encore géocodée, on centre d'abord la
    // carte dessus : un seul clic suffit pour tout le parcours.
    const commune = (this.communeTarget?.value || "").trim()
    if (commune && (!this.center || commune !== this._lastGeocoded)) {
      await this.searchCommune()
    } else if (this.center && this.inputRadiusKm() !== this.radiusKm) {
      // Rayon modifié depuis la dernière recherche (sans re-géocodage) : on le
      // prend en compte, sinon la génération repartirait sur l'ancien rayon.
      this.radiusChanged()
    }
    this.setGenerating(true)
    try {
      await this.loadForests()
    } finally {
      this.setGenerating(false)
    }
  }

  stopGeneration() {
    this.cancelGeneration = true
    if (this._overpassAbort) { try { this._overpassAbort.abort() } catch (e) {} }
    this._fetchControllers.forEach((c) => { try { c.abort() } catch (e) {} })
    this._fetchControllers = []
    this.setGenerating(false)
    this.showProgress(0, 1)
    this.progressLabelTargets.forEach((el) => { el.style.display = 'block'; el.textContent = 'Annulé' })
  }

  // État « recherche en cours » : bouton Stopper visible, bouton principal
  // transformé (spinner + étape), radar sur la carte, compteur live.
  setGenerating(active) {
    if (this.hasStopButtonTarget) this.stopButtonTarget.hidden = !active
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = active
      if (active) {
        this._generateLabel ??= this.generateButtonTarget.innerHTML
        this.setSearchStage("🛰️ Récupération des bois…")
      } else if (this._generateLabel) {
        this.generateButtonTarget.innerHTML = this._generateLabel
      }
    }
    if (active) {
      this.radarOn()
      this.showLiveBadge("🌲 Analyse en cours…")
    } else {
      this.radarOff()
      // Le toast de fin reste affiché quelques secondes après la génération.
      if (!this._toastTimer) this.hideLiveBadge()
    }
  }

  // Contenu du bouton principal pendant la recherche (spinner + étape).
  setSearchStage(label) {
    if (!this.hasGenerateButtonTarget) return
    this.generateButtonTarget.innerHTML = `
      <span class="inline-block size-4 rounded-full border-2 border-forest-950/25 border-t-forest-950 dark:border-forest-50/25 dark:border-t-forest-50 animate-spin"></span>
      <span>${label} <span data-search-pct></span></span>`
  }

  // --- Radar : balayage animé de la zone analysée ---

  radarOn() {
    if (this._radar || !this.hasMapTarget) return
    this._radar = document.createElement("div")
    this._radar.className = "map-radar"
    this.mapTarget.appendChild(this._radar)
    this._radarReposition = () => this.updateRadar()
    this.map.on("move zoom", this._radarReposition)
    this.updateRadar()
  }

  radarOff() {
    if (!this._radar) return
    this.map.off("move zoom", this._radarReposition)
    this._radar.remove()
    this._radar = null
  }

  // Calé sur le cercle de recherche s'il existe, sinon centré sur la vue.
  updateRadar() {
    if (!this._radar) return
    let center, rpx
    if (this.center && this.radiusKm) {
      center = this.map.latLngToContainerPoint([this.center.lat, this.center.lng])
      const east = this.map.latLngToContainerPoint([
        this.center.lat,
        this.center.lng + this.radiusKm / (111.32 * Math.cos(this.center.lat * Math.PI / 180))
      ])
      rpx = Math.abs(east.x - center.x)
    } else {
      const s = this.map.getSize()
      center = { x: s.x / 2, y: s.y / 2 }
      rpx = Math.min(s.x, s.y) * 0.35
    }
    Object.assign(this._radar.style, {
      left: `${center.x - rpx}px`, top: `${center.y - rpx}px`,
      width: `${rpx * 2}px`, height: `${rpx * 2}px`
    })
  }

  // --- Compteur live + toast de fin ---

  showLiveBadge(text) {
    if (!this.hasLiveBadgeTarget) return
    clearTimeout(this._toastTimer)
    this._toastTimer = null
    this.liveBadgeTextTarget.textContent = text
    this.liveBadgeTarget.hidden = false
  }

  hideLiveBadge() {
    if (this.hasLiveBadgeTarget) this.liveBadgeTarget.hidden = true
  }

  bumpLiveCounter(count) {
    if (!this.hasLiveBadgeTarget) return
    this.liveBadgeTextTarget.textContent = `🌲 ${count} coin${count > 1 ? "s" : ""} trouvé${count > 1 ? "s" : ""}…`
    this.liveBadgeTextTarget.classList.remove("badge-pop")
    void this.liveBadgeTextTarget.offsetWidth
    this.liveBadgeTextTarget.classList.add("badge-pop")
  }

  // Toast de fin : bilan de la recherche, auto-masqué.
  showSearchToast(results) {
    if (!this.hasLiveBadgeTarget) return
    if (results.length === 0) { this.hideLiveBadge(); return }
    const best = Math.round(Math.max(...results.map((r) => r.prob)) * 100)
    this.liveBadgeTextTarget.textContent = `✅ ${results.length} coin${results.length > 1 ? "s" : ""} trouvé${results.length > 1 ? "s" : ""} — meilleur : ${best} %`
    this.liveBadgeTarget.hidden = false
    this._toastTimer = setTimeout(() => { this._toastTimer = null; this.hideLiveBadge() }, 5000)
  }

  // Progression affichée partout à la fois : carte de la sidebar (desktop),
  // fine barre de la poignée + pastille sur la carte (mobile). D'où les
  // cibles au pluriel.
  showProgress(done, total) {
    const percent = Math.max(0, Math.min(100, Math.round((done / total) * 100)))
    const pctEl = this.hasGenerateButtonTarget && this.generateButtonTarget.querySelector("[data-search-pct]")
    if (pctEl) pctEl.textContent = `${percent} %`
    this.progressContainerTargets.forEach((el) => { el.style.display = 'block' })
    this.progressBarTargets.forEach((el) => { el.style.width = `${percent}%` })
    this.progressLabelTargets.forEach((el) => { el.style.display = 'block'; el.textContent = `${percent}%` })
    if (done >= total) {
      setTimeout(() => this.hideProgress(), 400)
    }
  }

  hideProgress() {
    this.progressContainerTargets.forEach((el) => { el.style.display = 'none' })
    this.progressLabelTargets.forEach((el) => { el.style.display = 'none' })
  }

  renderResults(items) {
    const container = this.resultsTarget
    container.innerHTML = ""
    if (!items || items.length === 0) return
    const min = this.getThreshold()
    const filtered = items.filter((i) => i.prob >= min)
    filtered.sort((a, b) => b.prob - a.prob)
    const top = filtered.slice(0, 100)
    top.forEach((it) => {
      container.appendChild(this.buildResultRow(it))
    })
  }

  buildResultRow(it) {
    const li = document.createElement('li')
    li.className = 'list-row'
    const prob = it.prob || 0
    const percent = Math.round(prob * 100)
    const gmaps = `https://www.google.com/maps?q=${it.center[0]},${it.center[1]}`
    li.innerHTML = `
      <span class="flex items-center gap-2 min-w-0">
        <button class="${this.probBadgeClass(prob)} cursor-pointer">${percent}%</button>
        <span class="truncate text-forest-800 capitalize">${this.escapeHtml(it.ft)}</span>
      </span>
      <a href="${gmaps}" target="_blank" title="Ouvrir dans Google Maps" class="text-forest-500 hover:text-forest-700 shrink-0">🗺️</a>`
    li.addEventListener('click', (e) => {
      if (e.target.closest('a')) return
      this.map.flyTo(it.center, 14)
      this.highlightPolygon(it.latlngs)
      // Mobile : replie le bottom sheet pour laisser voir le coin sur la carte
      this.dispatch('navigate', { target: li })
    })
    return li
  }

  appendResult(it) {
    const container = this.resultsTarget
    if (!container) return
    const min = this.getThreshold()
    if ((it.prob || 0) < min) return
    const li = this.buildResultRow(it)
    // Insertion triée par probabilité décroissante
    li.dataset.prob = String(it.prob || 0)
    let inserted = false
    for (const child of Array.from(container.children)) {
      const p = parseFloat(child.dataset.prob || '0')
      if ((it.prob || 0) > p) {
        container.insertBefore(li, child)
        inserted = true
        break
      }
    }
    if (!inserted) container.appendChild(li)
  }

  getThreshold() {
    const v = parseFloat(this.thresholdTarget?.value || "30")
    return Math.max(0, Math.min(100, isNaN(v) ? 30 : v)) / 100.0
  }

  highlightPolygon(latlngs) {
    const L = window.L
    this.highlightLayer.clearLayers()
    if (!latlngs || latlngs.length < 3) return
    const poly = L.polygon(latlngs, {
      color: '#f59e0b', // amber-500
      weight: 3,
      fillOpacity: 0.0,
      dashArray: '4 3',
      interactive: false
    }).addTo(this.highlightLayer)
    if (poly.bringToFront) poly.bringToFront()
  }

  flashPolygon(layer) {
    // Petit flash visuel à l'apparition
    const base = {
      color: layer.options.color,
      fillColor: layer.options.fillColor,
      fillOpacity: layer.options.fillOpacity,
      weight: layer.options.weight
    }
    let toggles = 0
    const maxToggles = 6 // ~3 cycles
    const timer = setInterval(() => {
      if (toggles % 2 === 0) {
        layer.setStyle({ color: '#fde047', fillOpacity: 0.6, weight: base.weight + 2 })
      } else {
        layer.setStyle(base)
      }
      toggles += 1
      if (toggles > maxToggles) {
        clearInterval(timer)
        layer.setStyle(base)
      }
    }, 120)
  }

  async loadForecast() {
    const center = this.center || this.map.getCenter()
    const lat = center.lat
    const lng = center.lng
    const mushroomId = this.selectTarget.value || this.mushroomIdValue
    const url = `/mushrooms/${mushroomId}/advice?lat=${lat}&lng=${lng}`
    const res = await fetch(url)
    const json = await res.json()
    this.renderForecast(json.days || [])
  }

  renderForecast(days) {
    const container = this.forecastResultsTarget
    container.innerHTML = ""
    if (!days || days.length === 0) {
      const li = document.createElement('li')
      li.className = 'text-sm text-forest-400 italic'
      li.textContent = "Aucune donnée de prévision disponible."
      container.appendChild(li)
      return
    }
    const top = days.slice(0, 10)
    top.forEach((d) => {
      const li = document.createElement('li')
      li.className = 'list-row cursor-default'
      const date = new Date(d.date)
      const score = d.score || 0
      const info = d.details || {}
      const dateLabel = date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
      li.innerHTML = `
        <span class="flex items-center gap-2 min-w-0">
          <span class="${this.probBadgeClass(score)}">${Math.round(score * 100)}%</span>
          <span class="text-forest-800 font-medium capitalize">${dateLabel}</span>
        </span>
        <span class="text-[11px] text-forest-500 whitespace-nowrap">💧 ${info.prcp_sum ?? '–'} mm · ${info.temp_min ?? '–'}–${info.temp_max ?? '–'}°C</span>`
      container.appendChild(li)
    })
  }

  translateForestType(ftRaw) {
    if (!ftRaw) return 'inconnu'
    const ft = String(ftRaw).toLowerCase().trim()
    const key = ft.replace(/[^a-z]/g, '') // supprime tirets/espaces/accents éventuels
    if (['broadleaved', 'deciduous'].includes(key)) return 'feuillus'
    if (['coniferous', 'needleleaved', 'conifer', 'coniferes', 'resineux'].includes(key)) return 'conifères'
    if (['feuillus', 'feuillu'].includes(key)) return 'feuillus'
    if (['mixed', 'mixte'].includes(key)) return 'mixte'
    // quelques autres variantes fréquentes
    if (['leaf', 'broadleaf'].includes(key)) return 'feuillus'
    if (['needle', 'needles'].includes(key)) return 'conifères'
    return ft
  }

  renderFeatures(geojson) {
    const L = window.L
    this.layer.clearLayers()
    if (!geojson || !geojson.features) return

    geojson.features.forEach((f) => {
      const [lng, lat] = f.geometry.coordinates
      const p = f.properties.probability || 0
      const br = f.properties.breakdown || {}
      const at = f.properties.observed_at
      const color = this.colorForProbability(p)
      const circle = L.circleMarker([lat, lng], {
        radius: 6,
        color,
        fillColor: color,
        fillOpacity: 0.7,
        weight: 1
      })
      const percent = Math.round(p * 100)
      const heading = this.bearing(this.map.getCenter().lat, this.map.getCenter().lng, lat, lng)
      const gmapsNav = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`
      const gsv = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=${Math.round(heading)}&pitch=5&fov=80`
      const html = `
        <div style="min-width: 250px">
          <div class="flex items-center justify-between gap-2">
            <div class="font-bold text-forest-900">🍄 Observation locale</div>
            <span class="${this.probBadgeClass(p)}">${percent}%</span>
          </div>
          <div class="text-xs text-forest-500 mt-0.5">Observée le ${this.formatDate(at)}</div>
          ${this.breakdownHtml(br)}
          <div class="mt-2 pt-2 border-t border-forest-100 text-sm flex gap-3">
            <a href="${gmapsNav}" target="_blank">🚗 Itinéraire</a>
            <a href="${gsv}" target="_blank">👁️ Street View</a>
          </div>
        </div>`
      circle.bindPopup(html)
      circle.addTo(this.layer)
    })
  }

  colorForProbability(p) {
    const r = Math.floor(255 * (1 - p))
    const g = Math.floor(255 * p)
    return `rgb(${r},${g},50)`
  }

  // Classe CSS du badge de probabilité selon le score
  probBadgeClass(p) {
    if (Math.round(p * 100) >= 100) return 'prob-badge prob-gold' // 100 % : badge trésor
    if (p >= 0.6) return 'prob-badge prob-high'
    if (p >= 0.35) return 'prob-badge prob-mid'
    return 'prob-badge prob-low'
  }

  // Détail du score sous forme de mini-barres dans les popups
  breakdownHtml(br) {
    const hasData = br && [br.seasonality, br.weather, br.habitat_match, br.soil_acidity, br.terrain_exposure, br.altitude_fit].some((v) => v != null)
    if (!hasData) return ''
    const rows = [
      ['Saison', br.seasonality],
      ['Météo', br.weather],
      ['Habitat', br.habitat_match],
      ['Sol', br.soil_acidity, br.soil_ph ? `pH~${Number(br.soil_ph).toFixed(1)}` : null],
      ['Exposition', br.terrain_exposure, br.slope_deg != null ? `${br.slope_deg}°` : null],
      ['Altitude', br.altitude_fit, br.elev_m != null ? `${Math.round(br.elev_m)} m` : null]
    ]
    const items = rows.map(([label, val, extra]) => {
      const pc = Math.round((val || 0) * 100)
      return `<div class="flex items-center gap-2 text-xs">
        <span class="w-16 shrink-0 text-forest-500">${label}</span>
        <span class="flex-1 h-1.5 rounded-full bg-forest-100 overflow-hidden"><span class="block h-full rounded-full" style="width:${pc}%; background:${this.colorForProbability(val || 0)}"></span></span>
        <span class="text-right tabular-nums font-medium text-forest-800 whitespace-nowrap">${pc}%${extra ? ` <span class="text-forest-400 font-normal">${extra}</span>` : ''}</span>
      </div>`
    }).join('')
    return `<div class="space-y-1 mt-1.5">${items}</div>`
  }

  renderExternalFeatures(geojson) {
    const L = window.L
    if (!geojson || !geojson.features) return
    geojson.features.forEach((f) => {
      const [lng, lat] = f.geometry.coordinates
      const at = f.properties.observed_at
      const name = f.properties.scientific_name || '—'
      const url = f.properties.source_url
      const provider = f.properties.provider || this.inferProviderFromDataset(f.properties.dataset)
      const p = f.properties.probability || 0
      const color = this.externalColorFor(provider)
      const circle = L.circleMarker([lat, lng], {
        radius: 6,
        color: color,
        fillColor: color,
        fillOpacity: Math.max(0.2, Math.min(0.9, p)),
        weight: 1
      })
      const heading = this.bearing(this.map.getCenter().lat, this.map.getCenter().lng, lat, lng)
      const gmapsNav = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`
      const gsv = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=${Math.round(heading)}&pitch=5&fov=80`
      const html = `
        <div style="min-width: 240px">
          <div class="flex items-center justify-between gap-2">
            <div class="font-bold text-forest-900">🌍 ${provider}</div>
            <span class="${this.probBadgeClass(p)}">${Math.round(p * 100)}%</span>
          </div>
          <div class="text-xs text-forest-500 italic mt-0.5">${name}</div>
          <div class="text-xs text-forest-500">Observée le ${this.formatDate(at)}</div>
          <div class="mt-2 pt-2 border-t border-forest-100 text-sm flex gap-3 flex-wrap">
            <a href="${gmapsNav}" target="_blank">🚗 Itinéraire</a>
            <a href="${gsv}" target="_blank">👁️ Street View</a>
            ${url ? `<a href="${url}" target="_blank">🔗 Détails</a>` : ''}
          </div>
        </div>`
      circle.bindPopup(html)
      circle.addTo(this.externalsLayer)
    })
  }

  externalColorFor(provider) {
    const key = (provider || '').toLowerCase()
    if (key.includes('gbif')) return '#ef4444' // red-500
    if (key.includes('inaturalist')) return '#10b981' // emerald-500
    if (key.includes('openobs')) return '#2563eb' // blue-600
    if (key.includes('mushroom observer')) return '#a855f7' // purple-500
    return '#475569' // slate-600
  }

  inferProviderFromDataset(dataset) {
    const d = (dataset || '').toLowerCase()
    if (d.includes('inaturalist')) return 'iNaturalist'
    if (d.includes('openobs')) return 'OpenObs'
    return 'GBIF'
  }

  // --- Caching externes (localStorage, TTL 24h) ---
  externalCacheKey() {
    const mushroomId = this.selectTarget.value || this.mushroomIdValue
    if (this.center && this.radiusKm) {
      return `ext:${mushroomId}:center:${this.center.lat.toFixed(4)},${this.center.lng.toFixed(4)}:r${this.radiusKm}`
    } else {
      const b = this.map.getBounds()
      return `ext:${mushroomId}:bounds:${b.getSouth().toFixed(4)},${b.getNorth().toFixed(4)},${b.getWest().toFixed(4)},${b.getEast().toFixed(4)}`
    }
  }

  async loadExternalGeoJSON() {
    try {
      const key = this.externalCacheKey()
      const raw = localStorage.getItem(key)
      const now = Date.now()
      const ttl = 24 * 60 * 60 * 1000 // 24h
      if (raw) {
        try {
          const obj = JSON.parse(raw)
          if (obj && obj.ts && (now - obj.ts) < ttl && obj.data) {
            return obj.data
          }
        } catch (e) { /* ignore parse error */ }
      }
      const url = this.apiUrlExternal()
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      const json = await res.json()
      try { localStorage.setItem(key, JSON.stringify({ ts: now, data: json })) } catch (e) { /* quota exceeded */ }
      return json
    } catch (e) {
      return { type: 'FeatureCollection', features: [] }
    }
  }

  // Beacon de stats interne : la parcelle dont la popup vient d'être ouverte.
  // Fire-and-forget, jamais bloquant pour l'UI.
  trackForestClick(forestId, mushroomId, lat, lng) {
    try {
      const data = new URLSearchParams()
      if (forestId) data.append('forest_id', forestId)
      if (mushroomId) data.append('mushroom_id', mushroomId)
      data.append('lat', lat)
      data.append('lng', lng)
      navigator.sendBeacon('/track/forest_click', data)
    } catch (e) {}
  }

  async loadNearbySightings(containerId, lat, lng) {
    try {
      const params = new URLSearchParams({ center: `${lat},${lng}`, radius_km: '2', include_prob: '0' })
      const mushroomId = this.selectTarget.value || this.mushroomIdValue
      const res = await fetch(`/mushrooms/${mushroomId}/sightings.json?${params.toString()}`)
      const json = await res.json()
      const el = document.getElementById(containerId)
      if (!el) return
      const feats = (json.features || []).slice(0, 10)
      if (feats.length === 0) { el.textContent = 'Aucune observation récente'; return }
      const ul = document.createElement('ul')
      ul.className = 'list-disc pl-4'
      feats.forEach((f) => {
        const li = document.createElement('li')
        const at = this.formatDate(f.properties.observed_at)
        li.textContent = `Observation le ${at}`
        ul.appendChild(li)
      })
      el.innerHTML = ''
      el.appendChild(ul)
    } catch (e) {
      const el = document.getElementById(containerId)
      if (el) el.textContent = 'Erreur de chargement'
    }
  }

  formatDate(iso) {
    if (!iso) return '—'
    try {
      const d = new Date(iso)
      return d.toLocaleDateString()
    } catch (e) { return '—' }
  }

  bearing(lat1, lon1, lat2, lon2) {
    // Retourne l'azimut en degrés (0..360) du point 1 vers point 2
    const toRad = (x) => x * Math.PI / 180
    const toDeg = (x) => x * 180 / Math.PI
    const φ1 = toRad(lat1), φ2 = toRad(lat2)
    const Δλ = toRad(lon2 - lon1)
    const y = Math.sin(Δλ) * Math.cos(φ2)
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
    const θ = Math.atan2(y, x)
    return (toDeg(θ) + 360) % 360
  }

  // Variante silencieuse de searchCommune pour le centrage automatique :
  // ne fait rien si le champ est vide ou si la commune est déjà géocodée
  // (évite le double appel quand Entrée déclenche aussi "change").
  autoCenter() {
    clearTimeout(this._suggestTimer)
    const q = (this.communeTarget.value || "").trim()
    if (!q || q === this._lastGeocoded) return
    this.searchCommune({ silent: true })
  }

  // --- Autocomplétion de commune (geo.api.gouv.fr) ---

  scheduleSuggest() {
    clearTimeout(this._suggestTimer)
    const q = (this.communeTarget.value || "").trim()
    if (q.length < 2 || q === this._lastGeocoded) { this.hideSuggestions(); return }
    this._suggestTimer = setTimeout(() => this.fetchSuggestions(q), 250)
  }

  async fetchSuggestions(q) {
    const byPostcode = /^\d{5}$/.test(q)
    const url = byPostcode
      ? `https://geo.api.gouv.fr/communes?codePostal=${q}&fields=centre,departement&limit=6`
      : `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(q)}&fields=centre,departement&boost=population&limit=6`
    let arr
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      arr = await res.json()
    } catch (e) { console.warn('geo_api_failed', e); return }
    // La saisie a continué pendant la requête : résultat périmé.
    if ((this.communeTarget.value || "").trim() !== q) return
    this._suggestions = (arr || []).filter((c) => c.centre?.coordinates)
    this.renderSuggestions()
  }

  renderSuggestions() {
    if (!this.hasSuggestionsTarget) return
    const ul = this.suggestionsTarget
    ul.innerHTML = ""
    this._highlighted = -1
    if (!this._suggestions.length) { this.hideSuggestions(); return }
    this._suggestions.forEach((c, i) => {
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.className = "px-3 py-2 cursor-pointer flex items-baseline justify-between gap-2"
      const name = document.createElement("span")
      name.className = "font-medium text-forest-900 truncate"
      name.textContent = c.nom
      const dep = document.createElement("span")
      dep.className = "text-xs text-forest-400 shrink-0"
      dep.textContent = c.departement ? `${c.departement.nom} (${c.departement.code})` : ""
      li.append(name, dep)
      // mousedown (et pas click) : passe avant le blur du champ, qui ferme la liste.
      li.addEventListener("mousedown", (e) => { e.preventDefault(); this.selectSuggestion(i) })
      li.addEventListener("mouseenter", () => this.highlightSuggestion(i))
      ul.appendChild(li)
    })
    ul.hidden = false
    this.communeTarget.setAttribute("aria-expanded", "true")
  }

  highlightSuggestion(i) {
    this._highlighted = i
    Array.from(this.suggestionsTarget.children).forEach((li, j) => {
      li.classList.toggle("bg-forest-100", j === i)
    })
  }

  hideSuggestions() {
    this._suggestions = []
    this._highlighted = -1
    if (!this.hasSuggestionsTarget) return
    this.suggestionsTarget.hidden = true
    this.suggestionsTarget.innerHTML = ""
    this.communeTarget.setAttribute("aria-expanded", "false")
  }

  selectSuggestion(i) {
    const c = this._suggestions?.[i]
    if (!c) return
    const [lng, lat] = c.centre.coordinates
    this.communeTarget.value = c.nom
    this.applyCommune({ name: c.nom, lat, lng })
  }

  // Navigation clavier dans la liste : flèches, Entrée (sélectionne la
  // suggestion surlignée, sinon la première ; liste fermée = recherche
  // directe), Échap pour fermer.
  suggestionsKeydown(e) {
    const open = this.hasSuggestionsTarget && !this.suggestionsTarget.hidden &&
      (this._suggestions || []).length > 0
    if (e.key === "ArrowDown" && open) {
      e.preventDefault()
      this.highlightSuggestion((this._highlighted + 1) % this._suggestions.length)
    } else if (e.key === "ArrowUp" && open) {
      e.preventDefault()
      this.highlightSuggestion((this._highlighted - 1 + this._suggestions.length) % this._suggestions.length)
    } else if (e.key === "Escape" && open) {
      this.hideSuggestions()
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (open) this.selectSuggestion(this._highlighted >= 0 ? this._highlighted : 0)
      else this.searchCommune()
    }
  }

  // Centre la carte sur une commune résolue et précharge ses bois en tâche de
  // fond (PrefetchForestsJob côté serveur) : au clic « Trouver les coins »,
  // la zone est souvent déjà en base.
  applyCommune({ name, lat, lng }) {
    this.hideSuggestions()
    this.center = { lat, lng }
    this.radiusKm = this.inputRadiusKm()
    this._lastGeocoded = (this.communeTarget.value || "").trim() || name
    this.drawCircleOverlay()
    this.fitToRadius()
    this.reloadData()
    this.prefetchForests()
  }

  // Rayon saisi, borné aux limites du serveur (ForestsController::MAX_RADIUS_KM).
  inputRadiusKm() {
    const r = parseFloat(this.radiusTarget?.value || "")
    return isNaN(r) ? 10 : Math.max(1, Math.min(100, r))
  }

  radiusChanged() {
    if (!this.center) return
    this.radiusKm = this.inputRadiusKm()
    this.drawCircleOverlay()
    this.fitToRadius()
    this.reloadData()
    this.prefetchForests()
  }

  // Pastille de rayon cliquée : mémorise la valeur, met à jour l'état visuel
  // et ajuste la zone si une commune est déjà centrée.
  setRadius(e) {
    this.radiusTarget.value = e.currentTarget.dataset.radius
    this.syncRadiusChips()
    this.radiusChanged()
  }

  syncRadiusChips() {
    const current = String(this.inputRadiusKm())
    this.radiusChipTargets.forEach((chip) => {
      chip.classList.toggle("chip-toggle-active", chip.dataset.radius === current)
    })
  }

  // Ajuste le zoom à la zone de recherche : la carte cadre le cercle entier,
  // quel que soit le rayon (un setView à zoom fixe coupait les grands rayons).
  fitToRadius() {
    if (!this.center || !this.radiusKm) return
    const dLat = this.radiusKm / 111.0
    const dLng = this.radiusKm / (111.0 * Math.cos(this.center.lat * Math.PI / 180))
    this.map.fitBounds([
      [this.center.lat - dLat, this.center.lng - dLng],
      [this.center.lat + dLat, this.center.lng + dLng]
    ], { padding: [24, 24] })
  }

  // silent : mode centrage automatique — pas de messages d'erreur pendant la
  // frappe (un nom incomplet est normal, pas une erreur à signaler).
  async searchCommune({ silent = false } = {}) {
    const q = (this.communeTarget.value || "").trim()
    if (!q) { if (!silent) this.flashStatus('Saisissez une commune'); return }
    const hit = await this.geocodeCommune(q) || await this.geocodeNominatim(q)
    // La saisie a continué pendant la requête : ce résultat est périmé.
    if ((this.communeTarget.value || "").trim() !== q) return
    if (!hit) { if (!silent) this.flashStatus(`Commune introuvable : ${q}`); return }
    this.applyCommune(hit)
  }

  // Résolution par l'API Découpage administratif (nom ou code postal).
  async geocodeCommune(q) {
    const byPostcode = /^\d{5}$/.test(q)
    const url = byPostcode
      ? `https://geo.api.gouv.fr/communes?codePostal=${q}&fields=centre&limit=1`
      : `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(q)}&fields=centre&boost=population&limit=1`
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const arr = await res.json()
      const c = (arr || []).find((x) => x.centre?.coordinates)
      if (!c) return null
      const [lng, lat] = c.centre.coordinates
      return { name: c.nom, lat, lng }
    } catch (e) {
      console.warn('geo_api_failed', e)
      return null
    }
  }

  // Repli Nominatim : tout ce qui n'est pas une commune (lieu-dit, forêt…).
  async geocodeNominatim(q) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q + ', France')}&limit=1`
    try {
      const res = await fetch(url, { headers: { 'Accept-Language': 'fr' } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const arr = await res.json()
      if (!arr || arr.length === 0) return null
      return { name: q, lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) }
    } catch (e) {
      console.warn('nominatim_failed', e)
      return null
    }
  }

  // Pré-charge les bois de la zone en tâche de fond dès la commune choisie :
  // pendant que l'utilisateur règle l'espèce et le rayon, Overpass travaille
  // déjà — au clic « Trouver les coins », la zone est souvent en base.
  prefetchForests() {
    if (!this.center || !this.radiusKm) return
    try {
      const dLat = this.radiusKm / 111.0
      const dLng = this.radiusKm / (111.0 * Math.cos(this.center.lat * Math.PI / 180))
      navigator.sendBeacon('/forests/prefetch', new URLSearchParams({
        south: this.center.lat - dLat, north: this.center.lat + dLat,
        west: this.center.lng - dLng, east: this.center.lng + dLng
      }))
    } catch (e) {}
  }

  // Affiche brièvement un message dans les zones de statut (feedback utilisateur).
  flashStatus(message, ms = 2000) {
    this.progressLabelTargets.forEach((el) => { el.style.display = 'block'; el.textContent = message })
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => this.hideProgress(), ms)
  }

  clearCircle() {
    this.center = null
    this.radiusKm = null
    this._lastGeocoded = null
    this.circleLayer.clearLayers()
    this.reloadData()
  }

  drawCircleOverlay() {
    const L = window.L
    this.circleLayer.clearLayers()
    if (!this.center || !this.radiusKm) return
    L.circle([this.center.lat, this.center.lng], {
      radius: this.radiusKm * 1000,
      color: '#2563eb',
      fillColor: '#3b82f6',
      fillOpacity: 0.1,
      weight: 1,
      interactive: false
    }).addTo(this.circleLayer)
  }
}


